import { useEffect, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { Icon } from "./Icon.tsx";

/**
 * El modal de la plantilla, con una regla innegociable encima:
 *
 * **UN CLIC FUERA NO CIERRA NADA.** Es el gesto mas facil de hacer sin querer
 * —el pulgar rozando el borde, un clic que falla el boton— y aqui detras hay un
 * formulario largo. Se cierra por la X, por Cancelar, o por Escape cuando no
 * hay nada escrito. Nunca por accidente.
 *
 * Escape tampoco cierra si `dirty` es cierto: es un gesto deliberado, pero no
 * lo bastante deliberado como para tirar diez campos rellenos.
 *
 * **SE PINTA EN UN PORTAL, COLGANDO DE `document.body`**, y eso no es una
 * preferencia: es la correccion de un fallo que el propietario describio como
 * "comienza a parpadear un monton de veces y es molesto".
 *
 * Un `position: fixed` deja de medirse contra la PANTALLA en cuanto un ancestro
 * tiene `transform`: ese ancestro pasa a ser su bloque contenedor. El modal se
 * pintaba dentro de la tarjeta de la nota, y `.post:hover` aplica
 * `translateY(-2px)`. Entonces: el raton entra en el fondo del modal -que esta
 * DENTRO del post-, el post se cree apuntado, aplica el transform, el modal se
 * re-ancla al post y SALTA; al saltar ya no esta bajo el raton, el post pierde el
 * hover, el modal vuelve... y otra vez. Un bucle a la velocidad de la transicion.
 *
 * Medido: con el transform puesto, el modal pasaba de 1280x900 en (0,0) a
 * 968x514 en (288,86).
 *
 * Colgando de `body` no hay ancestro que pueda hacerlo, hoy ni cuando alguien
 * anada un `transform` a cualquier tarjeta dentro de la que se monte un modal.
 * Los eventos de React siguen subiendo por el arbol de componentes, no por el del
 * DOM, asi que nada de lo que hay dentro cambia.
 */
export function Modal(props: {
  title: string;
  narrow?: boolean;
  dirty?: boolean;
  onClose: () => void;
  footer: ReactNode;
  children: ReactNode;
}): ReactNode {
  const card = useRef<HTMLDivElement>(null);
  const dirty = props.dirty === true;

  /* `onClose` y `dirty` viajan en un ref y NO en las dependencias del efecto.
     Quien monta el modal escribe `onClose={() => setAlgo(null)}`, que es una
     funcion NUEVA en cada render del padre; con ella en las dependencias, un
     efecto que debe correr una vez al abrir se re-ejecutaba en cada repintado:
     la limpieza devolvia el foco a lo que habia detras y el efecto se lo volvia a
     llevar, y `body.overflow` iba y venia con el. Eso puede sacar el cursor de un
     campo mientras escribes. */
  const latest = useRef({ onClose: props.onClose, dirty });

  useEffect(() => {
    latest.current = { onClose: props.onClose, dirty };
  });

  useEffect(() => {
    const restore = document.activeElement;

    document.body.style.overflow = "hidden";

    /* React aplica el `autoFocus` de los hijos en la fase de layout, ANTES de
       que corra este efecto. Si el foco ya esta dentro de la tarjeta, no se le
       roba: el contenedor solo lo recibe cuando no lo tiene nadie, que es lo que
       pasa cuando el modal no trae ningun campo con `autoFocus`. */
    const inside = restore instanceof Node && card.current?.contains(restore) === true;

    if (!inside) card.current?.focus();

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && !latest.current.dirty) {
        latest.current.onClose();

        return;
      }

      /* Trampa de foco: con el modal abierto, el tabulador no debe pasear por
         la pagina de detras, que sigue ahi pero no se puede usar. */
      if (event.key !== "Tab" || card.current === null) return;

      const focusables = card.current.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (first === undefined || last === undefined) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";

      if (restore instanceof HTMLElement) restore.focus();
    };
    // Sin dependencias: se monta una vez al abrir y se limpia al cerrar. Lo que
    // cambia entre medias se lee del ref.
  }, []);

  return createPortal(
    <div className="modal open" role="dialog" aria-modal="true" aria-label={props.title}>
      <div
        className={props.narrow === true ? "modal-card narrow" : "modal-card"}
        ref={card}
        tabIndex={-1}
      >
        <header>
          <h3>{props.title}</h3>
          <button type="button" className="icon-btn" aria-label="Cerrar" onClick={props.onClose}>
            <Icon name="x" />
          </button>
        </header>

        <div className="modal-body">{props.children}</div>

        <footer>{props.footer}</footer>
      </div>
    </div>,
    document.body,
  );
}
